<template>
    <section class="main">
        <div class="page-wrapper">
            <button class="back-arrow" type="button" aria-label="Go back" @click="$router.back()">
                <i class="bi bi-arrow-left"></i>
            </button>

            <div class="header">
                <div class="header-top">
                    <h5>Verify Your Account</h5>
                    <p class="notice">
                        Enter the {{ otpLength }}-digit code sent to
                        <span v-if="phoneMasked">{{ phoneMasked }}</span>
                        <span v-else>your phone number</span>
                    </p>
                </div>

                <form action="submit" @submit.prevent="verifyCode" class="tenant-form">
                    <div class="otp-row" @paste="onPaste">
                        <input
                            v-for="(digit, index) in code"
                            :key="index"
                            ref="digitInputs"
                            type="text"
                            inputmode="numeric"
                            autocomplete="one-time-code"
                            maxlength="1"
                            class="otp-box"
                            :class="{ 'otp-box-error': !!error }"
                            :value="digit"
                            @input="onDigitInput(index, $event)"
                            @keydown="onKeydown(index, $event)"
                            @focus="$event.target.select()"
                        >
                    </div>

                    <p v-if="error" class="error-text">{{ error }}</p>

                    <div class="resend-row">
                        <span>Didn't get a code?</span>
                        <button
                            type="button"
                            class="btn-resend"
                            :disabled="resendCooldown > 0 || resending"
                            @click="resendCode"
                        >
                            <span v-if="resending">Sending...</span>
                            <span v-else-if="resendCooldown > 0">Resend in {{ resendCooldown }}s</span>
                            <span v-else>Resend code</span>
                        </button>
                    </div>

                    <div class="form-actions">
                        <button type="button" class="btn-cancel" @click="$router.back()">Cancel</button>
                        <button type="submit" class="btn-save" :disabled="!isComplete || loading">
                            <span v-if="loading">Verifying...</span>
                            <span v-else>Verify</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </section>
</template>

<script>
import { apiFetch } from "@/services/api.js";

export default {
    data() {
        return {
            userId: null,
            phoneMasked: "",
            otpLength: 6, // matches the 6-digit code from generateVerificationCode() on the backend
            code: ["", "", "", "", "", ""],
            loading: false,
            error: "",
            resending: false,
            resendCooldown: 0,
            resendTimer: null,
            API_BASE_URL: 'https://golang.roberms.com/api'//'https://golang.roberms.com/api'
        };
    },

    computed: {
        isComplete() {
            return this.code.every((d) => d !== "");
        },
        fullCode() {
            return this.code.join("");
        },
    },

    async mounted() {
        this.userId = this.$route.params.user_id || this.$route.query.user_id || null;
        this.phoneMasked = this.$route.query.phone || "";
        this.actionType = this.$route.query.action_type || null;

        this.$nextTick(() => {
            this.focusInput(0);
        });
    },

    beforeUnmount() {
        clearInterval(this.resendTimer);
    },

    methods: {
        focusInput(index) {
            const el = this.$refs.digitInputs && this.$refs.digitInputs[index];
            if (el) el.focus();
        },

        onDigitInput(index, event) {
            const raw = event.target.value.replace(/\D/g, "");

            if (!raw) {
                this.code.splice(index, 1, "");
                return;
            }

            // Handles fast typing / autofill dropping more than one character at once
            const chars = raw.split("");
            this.code.splice(index, 1, chars[chars.length - 1]);
            event.target.value = this.code[index];

            if (this.error) this.error = "";

            if (index < this.otpLength - 1) {
                this.$nextTick(() => this.focusInput(index + 1));
            } else if (this.isComplete) {
                event.target.blur();
                this.verifyCode();
            }
        },

        onKeydown(index, event) {
            if (event.key === "Backspace") {
                if (!this.code[index] && index > 0) {
                    this.code.splice(index - 1, 1, "");
                    this.$nextTick(() => this.focusInput(index - 1));
                }
            } else if (event.key === "ArrowLeft" && index > 0) {
                this.focusInput(index - 1);
            } else if (event.key === "ArrowRight" && index < this.otpLength - 1) {
                this.focusInput(index + 1);
            }
        },

        onPaste(event) {
            event.preventDefault();
            const text = (event.clipboardData || window.clipboardData).getData("text");
            const digits = text.replace(/\D/g, "").slice(0, this.otpLength).split("");

            if (!digits.length) return;

            digits.forEach((d, i) => this.code.splice(i, 1, d));

            const nextIndex = Math.min(digits.length, this.otpLength - 1);
            this.$nextTick(() => this.focusInput(nextIndex));

            if (this.isComplete) this.verifyCode();
        },

        clearCode() {
            this.code = new Array(this.otpLength).fill("");
            this.$nextTick(() => this.focusInput(0));
        },

        async verifyCode() {
            if (!this.isComplete || this.loading) return;

            this.loading = true;
            this.error = "";

            const token = localStorage.getItem('token')
            const userId = localStorage.getItem('userId')

            try {
                const res = await fetch(`${this.API_BASE_URL}/verify-otp`, {
                    method: "POST",
                    headers:{
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ verification_code: this.fullCode, user_id:userId, action_type:this.actionType }),
                });

                const data =await res.json();

                if (res.status === 200 && data.success) {
                    const redirectMap = {
                        edit_tenant: '/tenants',
                        edit_profile: '/profile',
                    };
                    const destination = redirectMap[this.actionType] || '/tenants'; 

                    this.$router.push(destination);
                } else {
                    this.error = (res && res.error) || "Invalid verification code. Please try again.";
                    this.clearCode();
                }
            } catch (err) {
                console.log("Verify error", err);
                this.error = "Something went wrong. Please try again.";
                this.clearCode();
            } finally {
                this.loading = false;
            }
        },

        async resendCode() {
            if (this.resendCooldown > 0 || this.resending) return;

            this.resending = true;
            this.error = "";

            try {
                const res = await apiFetch(`${this.API_BASE_URL}/resend-verification`, {
                    method: "POST",
                    body: JSON.stringify({ user_id: this.userId }),
                });

                if (res && res.success) {
                    this.startCooldown();
                } else {
                    this.error = (res && res.error) || "Could not resend code. Please try again.";
                }
            } catch (err) {
                console.log("Resend error", err);
                this.error = "Could not resend code. Please try again.";
            } finally {
                this.resending = false;
            }
        },

        startCooldown() {
            this.resendCooldown = 30;
            clearInterval(this.resendTimer);
            this.resendTimer = setInterval(() => {
                this.resendCooldown -= 1;
                if (this.resendCooldown <= 0) clearInterval(this.resendTimer);
            }, 1000);
        },
    },
};
</script>

<style scoped>
.main {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    background: var(--dash-bg);
    padding: 2rem;
    box-sizing: border-box;
}

.page-wrapper {
    position: relative;
    width: 100%;
    max-width: 620px;
}

.back-arrow {
    position: absolute;
    top: -1.25rem;
    left: -1.25rem;
    height: 44px;
    width: 44px;
    border: none;
    border-radius: 50%;
    background-color: var(--dash-primary-hover);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    cursor: pointer;
    box-shadow: var(--dash-shadow);
    transition: background 0.2s ease, transform 0.1s ease;
}

.back-arrow:hover {
    background-color: var(--dash-primary-ink);
}

.back-arrow:active {
    transform: scale(0.94);
}

.header {
    width: 100%;
    max-width: 620px;
    background: #ffffff;
    border: 1px solid var(--dash-line);
    border-radius: var(--dash-radius);
    padding: 2rem 2.25rem;
    box-shadow: var(--dash-shadow);
}

.header-top {
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--dash-line);
    text-align: center;
}

.header-top h5 {
    margin: 0 0 0.35rem 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--dash-ink);
}

.notice {
    margin: 0;
    font-size: 0.85rem;
    color: var(--dash-faint);
}

.notice span {
    color: var(--dash-muted);
    font-weight: 600;
    font-style: normal;
}

.tenant-form {
    display: flex;
    flex-direction: column;
    gap: 1.1rem;
}

.otp-row {
    display: flex;
    justify-content: center;
    gap: 0.75rem;
    margin: 0.25rem 0 0.25rem;
}

.otp-box {
    width: 48px;
    height: 56px;
    text-align: center;
    font-size: 1.4rem;
    font-weight: 700;
    border: 1px solid var(--dash-line-strong);
    border-radius: var(--dash-radius);
    color: var(--dash-ink);
    background: var(--dash-surface-2);
    box-sizing: border-box;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.otp-box:focus {
    outline: none;
    border-color: var(--dash-primary);
    box-shadow: var(--dash-shadow);
    background: #ffffff;
}

.otp-box-error {
    border-color: var(--dash-danger);
}

.otp-box-error:focus {
    box-shadow: var(--dash-shadow);
}

.error-text {
    margin: -0.4rem 0 0;
    font-size: 0.85rem;
    color: var(--dash-danger);
    text-align: center;
}

.resend-row {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.85rem;
    color: var(--dash-faint);
}

.btn-resend {
    background: none;
    border: none;
    padding: 0;
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--dash-primary-hover);
    cursor: pointer;
}

.btn-resend:hover:not(:disabled) {
    color: var(--dash-primary-ink);
    text-decoration: underline;
}

.btn-resend:disabled {
    color: var(--dash-faint);
    cursor: not-allowed;
}

.form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 0.5rem;
    padding-top: 1.25rem;
    border-top: 1px solid var(--dash-line);
}

.btn-cancel,
.btn-save {
    padding: 0.6rem 1.4rem;
    border-radius: var(--dash-pill);
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid transparent;
    transition: background 0.2s ease, transform 0.05s ease, opacity 0.2s ease;
}

.btn-cancel {
    background: var(--dash-bg-alt);
    color: var(--dash-muted);
    border-color: var(--dash-line);
}

.btn-cancel:hover {
    background: var(--dash-line);
}

.btn-save {
    background: var(--dash-primary);
    color: #ffffff;
}

.btn-save:hover:not(:disabled) {
    background: var(--dash-primary-hover);
}

.btn-save:disabled {
    background: var(--dash-alt-soft);
    cursor: not-allowed;
}

.btn-cancel:active,
.btn-save:active {
    transform: scale(0.98);
}

@media (max-width: 480px) {
    .otp-box {
        width: 40px;
        height: 48px;
        font-size: 1.2rem;
    }

    .otp-row {
        gap: 0.5rem;
    }
}
</style>